import React, { useState } from "react"
import { LogIn, ShieldCheck, Sparkles } from "lucide-react"

function LoginPage({ onLogin, error }) {
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [localError, setLocalError] = useState("")

  const handleLogin = async () => {
    if (!onLogin || isSigningIn) return
    setLocalError("")
    setIsSigningIn(true)
    try {
      await onLogin()
    } catch (err) {
      setLocalError(err?.message || "Unable to sign in right now.")
    } finally {
      setIsSigningIn(false)
    }
  }

  const message = localError || error

  return (
    <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center px-6 py-10 relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-72 h-72 bg-purple-100 blur-3xl -top-10 -left-20"></div>
        <div className="absolute w-80 h-80 bg-fuchsia-100 blur-3xl bottom-0 right-0"></div>
      </div>

      <div className="relative w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
        <div className="bg-white border border-purple-100 rounded-2xl p-10 shadow-xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-xl">
              N
            </div>
            <div>
              <p className="text-sm text-purple-600 uppercase tracking-widest">Neo Access</p>
              <h1 className="text-3xl font-bold text-slate-900">Sign in to continue</h1>
            </div>
          </div>

          <p className="text-slate-700 leading-relaxed mb-8">
            Connect with Google to unlock your AI investing workspace, save portfolios, and sync insights across devices.
            Security-first authentication built for allocators and funds.
          </p>

          <button
            onClick={handleLogin}
            disabled={isSigningIn}
            className={`w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-semibold text-lg transition-all transform hover:-translate-y-0.5 ${
              isSigningIn
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white hover:shadow-xl hover:shadow-purple-200"
            }`}
          >
            <span className="w-6 h-6 rounded-full bg-white overflow-hidden flex items-center justify-center">
              <svg className="w-5 h-5" viewBox="0 0 48 48">
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6 1.54 7.38 2.83l5.4-5.26C33.89 4.01 29.54 2 24 2 14.82 2 7.09 7.99 3.7 16.17l6.91 5.36C12.5 13.26 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.5 24.5c0-1.56-.14-3.04-.4-4.5H24v9h12.7c-.56 2.88-2.2 5.32-4.7 6.96l7.2 5.59C43.89 37.4 46.5 31.5 46.5 24.5z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.61 28.67c-.5-1.48-.78-3.05-.78-4.67s.28-3.2.78-4.67L3.7 13.97C2.39 16.92 1.66 20.14 1.66 24s.73 7.08 2.04 10.03l6.91-5.36z"
                />
                <path
                  fill="#34A853"
                  d="M24 46c6.12 0 11.26-2.02 15.01-5.5l-7.2-5.59c-2 1.35-4.56 2.15-7.81 2.15-6.26 0-11.5-3.76-13.39-9.03l-6.91 5.36C7.09 40.01 14.82 46 24 46z"
                />
                <path fill="none" d="M0 0h48v48H0z" />
              </svg>
            </span>
            {isSigningIn ? "Connecting..." : "Continue with Google"}
            <LogIn className="w-5 h-5" />
          </button>

          {message && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {message}
            </div>
          )}

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-purple-100 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-slate-900 font-semibold mb-2">
                <ShieldCheck className="w-4 h-4 text-green-600" /> Secure
              </div>
              <p className="text-sm text-slate-700">OAuth2 sign-in powered by Firebase keeps your credentials safe.</p>
            </div>
            <div className="bg-white border border-purple-100 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-slate-900 font-semibold mb-2">
                <Sparkles className="w-4 h-4 text-amber-500" /> Fast Access
              </div>
              <p className="text-sm text-slate-700">Single-click login moves you into your investing cockpit.</p>
            </div>
            <div className="bg-white border border-purple-100 rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-slate-900 font-semibold mb-2">
                <LogIn className="w-4 h-4 text-purple-500" /> Always On
              </div>
              <p className="text-sm text-slate-700">Stay signed in across sessions so your data is always synced.</p>
            </div>
          </div>
        </div>

        <div className="text-slate-900 space-y-6">
          <div className="bg-white border border-purple-100 rounded-2xl p-8 shadow-xl">
            <p className="text-sm uppercase tracking-[0.35em] text-purple-600 mb-3">Neo</p>
            <h2 className="text-4xl font-bold leading-tight mb-4">
              Your AI investment desk,
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-700 via-fuchsia-600 to-amber-400">
                secured by Google Sign-In
              </span>
            </h2>
            <p className="text-slate-700">
              Authenticate once, then explore the bubble intelligence map, export portfolios, and receive AI-grade insights
              tuned to your mandate.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-100 rounded-xl p-6">
              <p className="text-sm text-purple-700 mb-2">Unified Identity</p>
              <p className="text-lg font-semibold text-slate-900">Carry your watchlists and saved analyses anywhere you sign in.</p>
            </div>
            <div className="bg-white border border-purple-100 rounded-xl p-6 shadow-sm">
              <p className="text-sm text-slate-700 mb-2">Zero Friction</p>
              <p className="text-lg font-semibold text-slate-900">One tap to access, no passwords to remember, built on Firebase auth.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
